import * as actionTypes from "../../constants/actionTypes";
import * as componentTypes from "../../constants/componentTypes";
import * as methods from "../../constants/methods";
import * as handlers from "../../constants/handlers";
import * as resultCodes from "../../constants/resultCodes";
import * as settings from "../../constants/settings";
import * as statisticsTabActions from "../../actions/statisticsTabActions";
import * as getterHelper from "../../helpers/getterHelper";
import * as messageHelper from "../../helpers/messageHelper";
import * as modelHelper from "../../helpers/modelHelper";
import * as responseHelper from "../../helpers/responseHelper";
import * as windowService from "../windowService";

export const processWebsocketResponse = (tabId, method, response, state) => {
    switch (method) {
        case handlers.GET_STATISTICS:
            if (!responseHelper.isGetStatisticsValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                {
                    type: actionTypes.STATISTICS_TAB_SAVE_FORMULA_LIST,
                    payload: {
                        formulaList: modelHelper.mapStatsToObjects(response.data.stats)
                    }
                }
            ];
        case handlers.GET_STATISTICS_WITH_OFFSET:
            if (!responseHelper.isGetStatisticsValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                {
                    type: actionTypes.STATISTICS_TAB_SAVE_FORMULA_LIST_WITH_OFFSET,
                    payload: {
                        tabId: tabId,
                        formulaList: modelHelper.mapStatsToObjects(response.data.stats)
                    }
                }
            ];
        case handlers.GET_FORMULA_DATA_FROM_STATS:
        case handlers.GET_FORMULA_META_DATA_FROM_STATS:
            if (!responseHelper.isGetDataFromStatsFormatValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            let statDetails = responseHelper.processDataFromStatDetails(
                response.data,
                state.settings.shotsName !== null ? state.settings.shotsName : settings.UNIT_SHOT,
                state.global.colorantList,
                state.settings.colorantPanelOrderBy,
                state.settings.validateTotalCntAmounts,
                state.settings.validateOverfill,
                state.settings.validateMinDosableAmount,
                state.settings.colorantBatchNumberLogic !== settings.COLORANT_BATCH_NUMBER_LOGIC_DISABLED,
                state.settings.validateColorantBaseRules,
                state.settings.colorantBatchForMultipleCans,
                state.settings.tintometricSystemsEnabled,
                state.settings.generateColorPreview
            );

            let statRequests = [
                {
                    type: actionTypes.SAVE_FORMULA_DATA_FROM_STATS,
                    payload: {
                        tabId: tabId,
                        ...statDetails
                    }
                }
            ];

            if (method === handlers.GET_FORMULA_DATA_FROM_STATS) {
                if (statDetails.formula !== null) {
                    statRequests.push({
                        type: actionTypes.MACHINE_VALIDATE_DISPENSED_FORMULA,
                        payload: {
                            tabId: tabId,
                            formula: statDetails.formula.getColorantVolumes(),
                            numCanDone: statDetails.numCanDone,
                            numCanTotal: statDetails.numCanTotal,
                            customCan: statDetails.package?.isCustom() ?? false
                        }
                    });
                }

                let invalidColorantList = getterHelper.getInvalidCalculatedFormulaColorants(
                    response.data.formula.colorants,
                    state.global.colorantList
                );

                if (invalidColorantList.length > 0) {
                    statRequests.push(messageHelper.getWarningMessageAction("flashMessage.invalidFormulaColorants"));

                    windowService.logInfo("Response data  : " + JSON.stringify(response.data));
                    windowService.logInfo(
                        "Selected formula contains invalid colorants: " + invalidColorantList.join(", ") + "."
                    );
                    windowService.logInfo(
                        "Available colorants: " + state.global.colorantList.map((c) => c.name).join(", ") + "."
                    );
                }

                return statRequests;
            }

            let statsDetailRequest = state.server.requests.find((request) => request.id === response.sequence);

            let nextAction = null;
            if (statsDetailRequest !== undefined && statsDetailRequest.next !== null) {
                if (statsDetailRequest.next.type === actionTypes.OPEN_NEW_MAIN_TAB) {
                    if (
                        statDetails.active &&
                        getterHelper.getNumberOfFormulaTabs(state.navigation.tabList) < state.settings.maxNumberOfTabs
                    ) {
                        nextAction = statsDetailRequest.next;
                    }
                } else {
                    nextAction = statsDetailRequest.next;
                }
            }

            if (statDetails.base !== null && statDetails.product !== null && statDetails.package !== null) {
                statRequests.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PRODUCT_IMAGE,
                        params: {
                            productName: statDetails.product.name,
                            baseName: statDetails.base.name,
                            packageName: statDetails.package.name,
                            online: state.settings.imagesSource === settings.SOURCE_ONLINE
                        }
                    },
                    method: handlers.GET_PRODUCT_IMAGE,
                    tabId: tabId,
                    next: nextAction
                });
            } else {
                if (nextAction !== null) {
                    statRequests.push(nextAction);
                }
            }

            return statRequests;
        case handlers.SET_STATISTIC_HIDDEN:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            return [
                {
                    type: actionTypes.STATISTICS_LIST_UPDATE,
                    payload: {
                        tabId: tabId
                    }
                }
            ];
        case handlers.SET_STATISTIC_MORE_INFO:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            let statsId = state.formula[tabId] ? state.formula[tabId].statisticsId : null;

            if (statsId === null) {
                return [];
            }

            return [
                {
                    type: actionTypes.STATISTICS_LIST_UPDATE,
                    payload: {
                        tabId: tabId
                    }
                },
                statisticsTabActions.loadFormulaDataFromStatistics(tabId, statsId)
            ];
        case handlers.GET_CORRECTION_HISTORY:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            return [
                {
                    type: actionTypes.SAVE_CORRECTION_HISTORY,
                    payload: {
                        correctionHistory: modelHelper.mapStatCorrectionsToObjects([response.data])
                    }
                },
                {
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        tabId: tabId,
                        type: componentTypes.CORRECTION_HISTORY_MODAL
                    }
                }
            ];
        case handlers.EXPORT_STATISTICS_HISTORY:
            if (response.result !== resultCodes.RESULT_OK) {
                return [
                    messageHelper.getFlashMessage(response),
                    {
                        type: actionTypes.NAVIGATION_CLOSE_MODAL,
                        payload: {
                            type: componentTypes.LOADING_MODAL
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.LOADING_MODAL
                    }
                }
            ];
        default:
            return [
                {
                    type: actionTypes.UNKNOWN_ACTION
                }
            ];
    }
};
